<i18n src="./resources/locales.json"></i18n>

<template>
  <div id="EcsThemeShowcase">
    <v-row class="ma-8">
      <v-select
        v-model="selectedTheme"
        :items="themeList"
        item-value="_id"
        item-text="name"
        :label="$t('theme')"
        class="mr-6 theme-select"
        :loading="loadingThemes"
        @change="changeTheme"
      />

      <v-switch v-model="isDarkMode" class="theme-toggle" :label="$t('darkMode')" @change="changeThemeMode" />
    </v-row>

    <v-row class="showcase-tabs-container">
      <v-tabs v-model="tab" background-color="primary" dark>
        <v-tab> Color Palette </v-tab>
        <v-tab> Telephony </v-tab>
        <v-tab> Chats </v-tab>
        <v-tab> Emails </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab" class="tab-content">
        <v-tab-item class="pb-12 mb-12">
          <div class="base-container">
            <div class="deep-control-container">
              <v-card class="box" color="info">
                {{ $t('info').toUpperCase() }}
                <div class="details">HEX {{ theme.info }}</div>
              </v-card>
              <v-card class="box" color="accent">
                {{ $t('accent').toUpperCase() }}
                <div class="details">HEX {{ theme.accent }}</div>
              </v-card>
              <v-card class="box" color="secondary">
                {{ $t('secondary').toUpperCase() }}
                <div class="details">HEX {{ theme.secondary }}</div>
              </v-card>
              <v-card class="box" color="primary">
                {{ $t('primary').toUpperCase() }}
                <div class="details">HEX {{ theme.primary }}</div>
              </v-card>
              <v-card class="box" color="surface">
                {{ $t('surface').toUpperCase() }}
                <div class="details">HEX {{ theme.surface }}</div>
              </v-card>
              <v-card class="box" color="success">
                {{ $t('success').toUpperCase() }}
                <div class="details">HEX {{ theme.success }}</div>
              </v-card>
              <v-card class="box" color="error">
                {{ $t('error').toUpperCase() }}
                <div class="details">HEX {{ theme.error }}</div>
              </v-card>
              <v-card class="box" color="warning">
                {{ $t('warning').toUpperCase() }}
                <div class="details">HEX {{ theme.warning }}</div>
              </v-card>
            </div>
          </div>

          <v-row v-for="color in mainColorsPalette" :key="color" class="lighten-darken-container">
            <div :class="`box small ${color} lighten-5`">
              <div v-once class="label">{{ $t('lighten') }} 50%</div>
            </div>
            <div :class="`box small ${color} lighten-4`">
              <div v-once class="label">{{ $t('lighten') }} 40%</div>
            </div>
            <div :class="`box small ${color} lighten-3`">
              <div v-once class="label">{{ $t('lighten') }} 30%</div>
            </div>
            <div :class="`box small ${color} lighten-2`">
              <div v-once class="label">{{ $t('lighten') }} 20%</div>
            </div>
            <div :class="`box small ${color} lighten-1`">
              <div v-once class="label">{{ $t('lighten') }} 10%</div>
            </div>
            <div :class="`box small ${color}`">
              <div class="label">{{ $t(`${color}`) }}</div>
            </div>
            <div :class="`box small ${color} darken-1`">
              <div v-once class="label">{{ $t('darken') }} 10%</div>
            </div>
            <div :class="`box small ${color} darken-2`">
              <div v-once class="label">{{ $t('darken') }} 20%</div>
            </div>
            <div :class="`box small ${color} darken-2`">
              <div v-once class="label">{{ $t('darken') }} 30%</div>
            </div>
            <div :class="`box small ${color} darken-3`">
              <div v-once class="label">{{ $t('darken') }} 40%</div>
            </div>
          </v-row>
        </v-tab-item>
        <v-tab-item>
          <EcsShowcaseInteraction :toolbar-options="telephonyOptions" :primary-action="telephonyPrimaryAction">
            <div class="showcase-interaction-content surface pt-6">
              <v-avatar size="150" class="telephony-avatar" color="var(--v-accent-base)">NT</v-avatar>

              <div class="call-widget surface lighten-1 elevation-8">
                <div class="call-widget-timer">00:21:13</div>

                <v-divider class="mx-4" vertical />

                <v-tooltip v-for="(item, index) in callWidgetOptions" :key="index" top>
                  <template v-slot:activator="{ on }">
                    <v-btn class="control" icon v-on="on">
                      <v-icon>{{ item.icon }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ item.label }}</span>
                </v-tooltip>
              </div>
            </div>
          </EcsShowcaseInteraction>
        </v-tab-item>
        <v-tab-item>
          <EcsShowcaseInteraction :toolbar-options="chatOptions" :primary-action="chatPrimaryAction">
            <div class="showcase-interaction-content surface">
              <EcsConversationMessage
                v-for="(message, index) in chatMessages"
                :key="index"
                :message="message"
                :is-sms="false"
                :receptor="{}"
              />
            </div>
            <EcsSubmitInput placeholder="Message Nikola" class="ma-3" />
          </EcsShowcaseInteraction>
        </v-tab-item>
        <v-tab-item>
          <EcsShowcaseInteraction :toolbar-options="emailOptions" :primary-action="emailPrimaryAction" no-chips>
            <div class="showcase-interaction-content surface pa-3">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut placerat libero ut velit malesuada blandit.
              Suspendisse pretium malesuada tortor at dignissim. Lorem ipsum dolor sit amet, consectetur adipiscing
              elit. Aliquam quis dolor tristique lacus bibendum rutrum eu vel odio. Pellentesque convallis ligula justo,
              nec tincidunt lacus pellentesque sed. Nunc imperdiet ante placerat, dignissim enim ut, posuere mi. Morbi
              in velit ligula. Duis posuere porttitor sem eu sagittis. Vestibulum vel dapibus enim.
            </div>
          </EcsShowcaseInteraction>
        </v-tab-item>
      </v-tabs-items>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ThemeUtility from '@/components/ecs-theme/ecs-theme-utility';
import { THEME_DARK_MODE, THEME_LIGHT_MODE } from '@/constants/themeConstants';
import EcsSubmitInput from '@/components/ecs-submit-input/EcsSubmitInput';
import EcsShowcaseInteraction from '@/components/ecs-theme/EcsShowcaseInteraction';
import EcsConversationMessage from '@/components/ecs-conversation/ecs-conversation-message/EcsConversationMessage';

const FORWARD_ICON = 'mdi-forward';

export default {
  name: 'EcsThemeShowcase',

  components: { EcsConversationMessage, EcsSubmitInput, EcsShowcaseInteraction },

  data: () => ({
    isDarkMode: false,
    tab: 0,
    theme: {},
    selectedTheme: '',
    themeList: [],
    loadingThemes: false,
    mainColorsPalette: ['surface', 'primary', 'secondary', 'accent'],
    chatOptions: {
      hold: {
        icon: 'mdi-pause',
        label: 'Hold'
      },
      transfer: {
        icon: FORWARD_ICON,
        label: 'Transfer'
      },
      consult: {
        icon: 'mdi-account-plus',
        label: 'Consult'
      }
    },
    telephonyOptions: {
      hold: {
        icon: 'mdi-pause',
        label: 'Hold'
      },
      transfer: {
        icon: FORWARD_ICON,
        label: 'Transfer'
      },
      consult: {
        icon: 'mdi-account-plus',
        label: 'Consult'
      }
    },
    emailOptions: {
      transfer: {
        icon: FORWARD_ICON,
        label: 'Transfer'
      }
    },
    emailPrimaryAction: {
      icon: 'mdi-reply',
      label: 'Reply'
    },
    chatPrimaryAction: {
      icon: 'mdi-message-bulleted-off',
      label: 'End Chat'
    },
    telephonyPrimaryAction: {
      icon: 'mdi-phone-hangup',
      label: 'End Call'
    },
    chatMessages: [],
    callWidgetOptions: [
      {
        icon: 'mdi-microphone',
        label: 'Mute Microphone'
      },
      {
        icon: 'mdi-phone-paused',
        label: 'Resume Call'
      },
      {
        icon: 'mdi-dialpad',
        label: 'Dialpad'
      }
    ]
  }),

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('SETTINGS', ['currentUserSettings']),

    themeMode() {
      return this.$vuetify.theme.dark ? THEME_DARK_MODE : THEME_LIGHT_MODE;
    }
  },

  mounted() {
    this.retrieveInitialData();
    this.isDarkMode = this.$vuetify.theme.dark;
  },

  created() {
    this.loadShowcaseTheme();
    this.retrieveInitialData();
    this.chatMessages = [
      {
        i18nKey: '3f274f41-0e85-4b2e-99a5-df791ac1ade8',
        icon: 'mdi-play-circle',
        oldState: '0844b4a8-89c9-4c14-a1b7-050f81a6d012',
        originator: {},
        timestamp: new Date(),
        type: 'SYSTEM'
      },
      {
        content: 'How may I help you today?',
        icon: 'mdi-lightbulb-on',
        originator: { name: 'Nikola Tesla (ntesla@eipmail.com)' },
        timestamp: new Date(),
        type: 'SYSTEM'
      },
      {
        content: `I am going to review your account further. I'll be back in 2 minutes.`,
        originator: { id: this.loggedInAgent.id, name: this.loggedInAgent.name },
        timestamp: new Date(),
        type: 'REGULAR'
      },
      {
        content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
        originator: { name: 'Nikola Tesla (ntesla@eipmail.com)' },
        timestamp: new Date(),
        type: 'REGULAR'
      }
    ];
  },

  methods: {
    ...mapActions('SETTINGS', ['updateThemeMode', 'updateThemeId']),

    async changeThemeMode(isDarkMode) {
      isDarkMode ? ThemeUtility.setDarkMode() : ThemeUtility.setLightMode();

      const settings = this.currentUserSettings;
      settings.theme.mode = this.themeMode;

      await this.updateThemeMode(this.themeMode);
      this.loadShowcaseTheme();
    },

    changeTheme(themeId) {
      const theme = this.themeList.find(theme => theme._id === themeId);
      const settings = this.currentUserSettings;
      settings.theme._id = theme._id;

      this.updateThemeId(theme._id);

      ThemeUtility.setTheme(theme);

      this.theme = theme[this.themeMode];
      this.selectedTheme = themeId;
    },

    loadShowcaseTheme() {
      this.theme = this.$vuetify.theme.dark ? this.$vuetify.theme.themes.dark : this.$vuetify.theme.themes.light;
    },

    retrieveInitialData() {
      this.loadingThemes = true;
      ThemeUtility.getAllThemes()
        .then(async themes => {
          this.themeList = themes || [];
          await this.$nextTick();
          this.selectedTheme = this.currentUserSettings.theme._id;
        })
        .catch(console.log)
        .finally(() => {
          this.loadingThemes = false;
          this.loadShowcaseTheme();
        });
    }
  }
};
</script>

<style scoped>
.showcase-tabs-container {
  width: calc(100% - 32px);
  margin: 16px;
  overflow: hidden;
}

.showcase-interaction-content {
  height: 264px;
}

.showcase-interaction-content .telephony-avatar {
  margin: 0 auto 20px;
  height: 150px;
  display: block;
  font-size: 62px;
  line-height: 150px;
  color: #fff;
}

.call-widget {
  width: 220px;
  display: flex;
  margin: 0 auto;
  border-radius: 4px;
}

.call-widget .call-widget-timer {
  line-height: 40px;
  padding: 0 0 0 12px;
}

.call-widget .control {
  border-radius: 0;
  height: 40px;
  width: 36px;
  margin-right: 8px;
}

.call-widget .control.v-btn {
  margin: 0;
}

.tab-content {
  width: 100%;
  display: block;
  padding: 40px;
}

.box {
  padding: 144px 16px 12px;
  width: 120px;
  height: 195px;
  float: left;
  font-size: 12px;
  font-weight: bold;
  border-radius: 4px;
  box-shadow: 0 0 5px #0004;
  margin: 4px;
  text-align: left;
  color: #fff;

  -webkit-transition: all 0.2s ease;
  -moz-transition: all 0.2s ease;
  -ms-transition: all 0.2s ease;
  -o-transition: all 0.2s ease;
  transition: all 0.2s ease;
}

.box.small {
  width: 60px;
  box-sizing: border-box;
  height: 80px;
  line-height: 16px;
  padding: 12px;
  text-align: center;
  border-radius: 8px;
  margin: 0 6px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
  position: relative;
}

.box.small .label {
  color: #777;
  text-transform: capitalize;
  font-size: 11px;
  position: absolute;
  top: 86px;
  right: 0;
  left: 0;
  margin: 0 auto;
}

.details {
  font-size: 11px;
  font-weight: normal;
  opacity: 0.75;
  text-transform: uppercase;
}

.theme--light .surface {
  color: #444;
}

.clear-fix {
  clear: both;
}

.v-select {
  width: 200px;
}

.v-select,
.v-btn {
  float: left;
}

.v-btn {
  display: inline-block;
  margin: 12px;
  font-size: 12px;
}
h3 {
  padding: 0 0 12px;
  text-align: left;
}
p {
  font-size: 13px;
  text-align: left;
}
.base-container {
  position: relative;
  min-width: 800px;
  height: 300px;
  perspective: 800px;
  perspective-origin: center center;
  margin: 0 auto;
}
.deep-control-container {
  width: 100%;
  height: 100%;
  transform: translateZ(0px);
  perspective: inherit;
}
.base-container .box {
  position: absolute;
  margin: auto auto;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;

  -webkit-transition: all 0.2s linear;
  -moz-transition: all 0.2s linear;
  -ms-transition: all 0.2s linear;
  -o-transition: all 0.2s linear;
  transition: all 0.2s linear;
}

.base-container .box:nth-child(1) {
  z-index: 0;
  transform: translateZ(0px) translateX(-390px) rotateY(45deg);
}
.base-container .box:nth-child(2) {
  z-index: 1;
  transform: translateZ(20px) translateX(-280px) rotateY(30deg);
}
.base-container .box:nth-child(3) {
  z-index: 2;
  transform: translateZ(50px) translateX(-170px) rotateY(20deg);
}
.base-container .box:nth-child(4) {
  z-index: 3;
  transform: translateZ(100px) translateX(-60px) rotateY(10deg);
}
.base-container .box:nth-child(5) {
  z-index: 2;
  text-align: right;
  transform: translateZ(100px) translateX(60px) rotateY(-10deg);
}
.base-container .box:nth-child(6) {
  z-index: 1;
  text-align: right;
  transform: translateZ(50px) translateX(170px) rotateY(-20deg);
}
.base-container .box:nth-child(7) {
  z-index: 0;
  text-align: right;
  transform: translateZ(20px) translateX(280px) rotateY(-30deg);
}
.base-container .box:nth-child(8) {
  z-index: -1;
  text-align: right;
  transform: translateZ(0px) translateX(390px) rotateY(-45deg);
}

.contrast-container label {
  float: left;
  text-align: left;
  font-size: 11px;
  font-weight: bold;
  margin: 20px 4px;
  width: 140px;
}

.contrast-container label.right {
  text-align: right;
  float: right;
}

.lighten-darken-container {
  width: 720px;
  margin: 0 auto;
}

.lighten-darken-container:not(:last-child) {
  padding-bottom: 60px;
}

.theme-select {
  width: 300px;
  flex: none;
}
</style>
